import styled from "@emotion/styled";
import { Spacer } from "components/general/Spacer/Spacer";
import { Text } from "components/general/Text";
import { SPRING_ANIMATION, Z_INDICES } from "config/consts";
import { motion } from "framer-motion";
import { noop } from "lodash-es";
import { CSSProperties, forwardRef, useLayoutEffect, useEffect, useMemo, useState } from "react";
import { GlobalStore } from "store/global/GlobalStore";
import { Token } from "store/global/TokenEnum";
import { addTokenToStore } from "utils/tokenSelectorUtils";
import bebopBinocularsImage from "../../img/trade-interface/bebop_binoculars.png";
import { TokenSelectorRow } from "./elements/TokenSelectorDropdownRow";
import { CounterParty } from "utils/tradeUtils";
import { useHover } from "usehooks-ts";
import { Amount } from "types/amount";
import { getIsUserConnected } from "utils/userUtils";

interface TokenSelectorDropdownProps {
  isHidden: boolean;
  counterParty: CounterParty;
  searchString: string;
  tokensToShow: Array<Token> | null;
  highlightedRowIdx: number | null;
  isLastHighlightedWithKeyboard: React.MutableRefObject<boolean>;
  dropdownBoundingClientRect: DOMRect | undefined;
  scrollToTop: () => void;
  setHighlightedRowIdx: React.Dispatch<React.SetStateAction<number | null>>;
  closeDropdown: () => void;
  isChangingToken?: Token | undefined;
}

export const TokenSelectorDropdown = forwardRef<HTMLDivElement, TokenSelectorDropdownProps>(
  (
    {
      isHidden,
      counterParty,
      searchString,
      tokensToShow,
      highlightedRowIdx,
      isLastHighlightedWithKeyboard,
      dropdownBoundingClientRect,
      scrollToTop,
      setHighlightedRowIdx,
      closeDropdown,
      isChangingToken,
    },
    ref
  ) => {
    const isHovered = useHover<HTMLDivElement>(ref as React.RefObject<HTMLDivElement>);
    const isUserConnected = getIsUserConnected();
    const balances = GlobalStore.useState(s => s.balances);
    const address = GlobalStore.useState(s => s.address);
    const balanceTokenAmountMap = useMemo(
      () =>
        balances
          .map(entry => entry.symbol)
          .reduce<Record<Token, Amount>>((prev, curr: Token, i) => {
            prev[curr] = balances[i].balance;
            return prev;
          }, {} as Record<Token, Amount>),
      [balances]
    );
    const firstTokenWithoutBalanceIdx = isUserConnected
      ? tokensToShow?.findIndex(symbol => balanceTokenAmountMap[symbol].value.eq(0)) ?? null
      : null;

    const rowCount = tokensToShow?.length ?? 0;
    const [dropdownHeight, setDropdownHeight] = useState<CSSProperties["height"]>(
      isHidden ? 0 : getDropdownHeight(rowCount)
    );

    useLayoutEffect(() => {
      let height: CSSProperties["height"] = getDropdownHeight(rowCount);
      if (rowCount === 0) {
        height = "fit-content";
      } else if (isHidden) {
        height = 0;
      }
      setDropdownHeight(height);
    }, [isHidden, rowCount]);

    useEffect(() => {
      if (!isHovered && !isLastHighlightedWithKeyboard.current) {
        setHighlightedRowIdx(null);
      }
    }, [isHovered, isLastHighlightedWithKeyboard, setHighlightedRowIdx]);

    if (tokensToShow == null || (address !== "" && balances.length === 0)) {
      const rowCount = 7;
      return (
        <Container
          ref={ref}
          initial={{ height: 0 }}
          animate={{ height: Math.min(357, rowCount * TOKEN_ROW_HEIGHT + 8 * 2) }}
          transition={{ duration: 0.16, ease: "easeOut" }}
          isHidden={isHidden}
        >
          {Array(rowCount)
            .fill(0)
            .map((_, i) => (
              <TokenSelectorRow
                key={i}
                symbol={Token.ETH}
                isLoading={true}
                addTokenToStore={noop}
                showSeparatorAbove={false}
                counterParty={"maker"}
                closeDropdown={noop}
                confirmHighlightedRowIdx={noop}
                isFirst={i === 0}
                isLastHighlightedWithKeyboard={isLastHighlightedWithKeyboard.current}
                dropdownBoundingClientRect={dropdownBoundingClientRect}
                scrollToTop={scrollToTop}
                isChangingToken={isChangingToken}
              />
            ))}
        </Container>
      );
    }

    return (
      <Container
        ref={ref}
        initial={{ height: 0 }}
        animate={{
          height: dropdownHeight,
          ...(rowCount === 0 ? { minHeight: `${CANNOT_FIND_TOKEN_INFO_MIN_HEIGHT}px` } : undefined),
        }}
        transition={SPRING_ANIMATION}
        isHidden={isHidden}
      >
        {tokensToShow.length === 0 ? (
          <CannotFindTokenInfo searchString={searchString} />
        ) : (
          <>
            {tokensToShow.map((symbol, i) => (
              <TokenSelectorRow
                key={symbol}
                symbol={symbol}
                addTokenToStore={() => addTokenToStore(counterParty, symbol)}
                isHighlighted={highlightedRowIdx != null && i === highlightedRowIdx}
                showSeparatorAbove={
                  firstTokenWithoutBalanceIdx !== 0 && i === firstTokenWithoutBalanceIdx
                }
                counterParty={counterParty}
                closeDropdown={closeDropdown}
                confirmHighlightedRowIdx={() => {
                  setHighlightedRowIdx(i);
                  isLastHighlightedWithKeyboard.current = false;
                }}
                isFirst={i === 0}
                isLastHighlightedWithKeyboard={isLastHighlightedWithKeyboard.current}
                dropdownBoundingClientRect={dropdownBoundingClientRect}
                scrollToTop={scrollToTop}
                isChangingToken={isChangingToken}
              />
            ))}
          </>
        )}
      </Container>
    );
  }
);

// Helpers

const TOKEN_ROW_HEIGHT = 61;
const CANNOT_FIND_TOKEN_INFO_MIN_HEIGHT = 201;
const MAX_ROWS_VISIBLE = 7;
export const MAX_DROPDOWN_HEIGHT = 357;

const getDropdownHeight = (rowCount: number) =>
  Math.min(MAX_DROPDOWN_HEIGHT, rowCount * TOKEN_ROW_HEIGHT + 8 * 2);

// Sub-components

const Container = styled(motion.div)<{ isHidden: boolean }>(({ theme, isHidden }) => {
  return {
    position: "absolute",
    zIndex: Z_INDICES.CONTENT_SURFACE,
    visibility: isHidden ? "hidden" : "initial",
    left: 8.5,
    display: "flex",
    flexDirection: "column",
    minWidth: "min(376px, 95%)",
    width: "min(376px, 80%)",
    maxHeight: getDropdownHeight(MAX_ROWS_VISIBLE),
    background: theme.surface.main.secondary.default,
    borderRadius: 24,
    padding: 8,
    overflow: "scroll",
    msOverflowStyle: "none" /* IE and Edge */,
    scrollbarWidth: "none" /* Firefox */,

    "::-webkit-scrollbar": {
      display: "none" /* Chrome, Safari and Opera */,
    },
  };
});

const CannotFindTokenInfo = ({ searchString }: { searchString: string }) => {
  const theme = GlobalStore.useState(s => s.theme);
  return (
    <CannotFindTokenInfoContainer>
      <img src={bebopBinocularsImage} alt="Googly-eyed binoculars" width={150} />
      <Spacer height={26} />
      <div style={{ width: "95%", overflowWrap: "break-word" }}>
        <Text as={"div"} textStyle="Title" size="XS">
          We can't find a token for{" "}
          <span style={{ color: theme.font.primary.default }}>'{searchString}'</span>
        </Text>
        <Spacer height={6} />
        <Text as={"div"} textStyle="Title" size="XS">
          Try editing your search or switch networks
        </Text>
      </div>
    </CannotFindTokenInfoContainer>
  );
};

const CannotFindTokenInfoContainer = styled.div(({ theme }) => {
  return {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: "24px 16px",
    color: theme.font.tertiary.default,
    textWrap: "wrap",
    textAlign: "center",
  };
});
